import type { ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { FreshnessMeta } from "@/lib/api";
import { backendDownCopy } from "@/lib/freshness";
import type { TabEntry } from "@/lib/tabs";
import { FreshnessLine } from "./FreshnessLine";
import { ModuleTabs, ModuleTabsSkeleton } from "./ModuleTabs";
import { PermissionBadge, PermissionGate } from "./PermissionGate";
import type { PermissionDecision } from "./PermissionGate";

interface ModuleViewportProps {
  /** From the registry — the shell never invents a module's name (I-5). */
  title: string;
  areaName: string;
  decision: PermissionDecision | null | undefined;
  ownerName?: string | null;
  tabs?: TabEntry[];
  activeTabKey?: string | null;
  onOpenTab?: (tabKey: string) => void;
  /** Live per-tab counts from the native module (registry `useNativeModuleTabHints`). */
  tabCounts?: Record<string, number>;
  /** The S8 envelope's `meta` for the CURRENTLY SHOWN tab. */
  meta?: FreshnessMeta | null;
  /** Data in flight — chrome stays, content skeletons (UX S4 loading). */
  pending?: boolean;
  /** The module's backend is unreachable. Named so the copy can say WHICH system. */
  backendDown?: { system: string; lastLoad: string | null } | null;
  /**
   * Drop the shell's own title and freshness line (operator decision, Damien
   * 2026-08-24: "remove these in each area, it's not the design").
   *
   * Set for a DOCKED surface only. A docked surface renders its own product
   * header inside the frame, so the shell's `<h1>` repeated the module name
   * directly above it; and the freshness line could only ever read "We can't
   * confirm this data's date", because a framed surface returns no S8 envelope
   * for the shell to read a date from. Both were noise on exactly the surfaces
   * that make up the whole portal today.
   *
   * This intentionally relaxes I-7 (the frame owns the title) and the UX v0.3
   * §6 W2a ordering for docked modules. A NATIVE module still gets both.
   *
   * **Be precise about what that means today: the freshness line reaches NO
   * ONE.** Every module in the portal is currently docked, so "the freshness
   * statement is non-optional" describes an invariant that holds for zero
   * modules in production (UX audit, 2026-08-27). It is not enforced, it is
   * SUSPENDED — deliberately, because a framed surface returns no S8 envelope
   * to read a date from, so the line could only ever have read "we can't
   * confirm this data's date". Do not read the rule as a live guarantee. The
   * day a native module ships is the moment to verify the line renders for it
   * and to add the regression test asserting it.
   *
   * The permission badge is kept in both modes: it is an access signal, not
   * chrome.
   */
  bare?: boolean;
  children?: ReactNode;
  now?: number;
}

/**
 * The frame every module renders inside (C3, I-7): title · permission badge ·
 * tab bar · freshness line · content.
 *
 * The vertical order is load-bearing, not cosmetic. The freshness statement
 * belongs to the content CURRENTLY SHOWN, so it sits between the tab that
 * selected that content and the content itself — above the tabs it would be
 * ambiguous which tab's data it describes (UX v0.3 §6 W2a).
 *
 * The other rule this frame enforces: chrome renders before, and independently
 * of, data. A module whose backend is down still shows its title, its tabs and
 * a stated reason — never a blank region (BR-11, UX S4 error).
 */
export function ModuleViewport({
  title,
  areaName,
  decision,
  ownerName = null,
  tabs = [],
  activeTabKey = null,
  tabCounts,
  onOpenTab,
  meta,
  pending = false,
  backendDown = null,
  bare = false,
  children,
  now,
}: ModuleViewportProps) {
  // A docked module with full edit rights has NOTHING to put in the header —
  // `PermissionBadge` returns null for `edit`, and `bare` already dropped the
  // title. What was left was an empty flex row with a margin: 8px of dead band
  // above every docked frame, on top of the gutter and the border (Damien,
  // 2026-08-27: "why don't the modules take up 100% of the space"). Render no
  // header at all rather than an empty one. A `view` module still gets it —
  // the badge is an access signal, not chrome.
  const headerIsEmpty = bare && decision !== "view";

  const header = (
    // Spacing below the header is set by what the header CARRIES. A native
    // module's `<h1>` needs the full gap under it; a docked module's header is
    // one small badge, and giving that badge a heading's worth of clearance is
    // what pushed a framed surface a whole dead band below the top of the
    // content area (Damien, 2026-08-26). Both values are the shell's own scale
    // (4 = 16px, 2 = 8px) — this is a tighter step on the scale, not a new one.
    <header
      className={`${bare ? "mb-2" : "mb-4"} flex flex-wrap items-center gap-3 ${bare ? "justify-end" : "justify-between"}`}
    >
      {bare ? null : <h1 className="text-2xl font-bold text-ks-ink">{title}</h1>}
      <PermissionBadge decision={decision} areaName={areaName} ownerName={ownerName} />
    </header>
  );

  // Deny by default (I-1). An unresolved or absent decision renders the frame
  // and states why — it never renders the module's content underneath.
  if (decision !== "edit" && decision !== "view") {
    return (
      <section aria-label={title}>
        {header}
        <Alert className="max-w-2xl">
          <AlertTitle>You don&rsquo;t have access to {title}.</AlertTitle>
          <AlertDescription>
            {ownerName ? `${ownerName} can grant it.` : "Damien can grant it."}
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  // A docked surface OWNS the remaining height: it scrolls inside its frame,
  // so the shell must hand it the room rather than size to it. A native module
  // still sizes to its content and lets the main gutter scroll — filling would
  // stretch a short page into a tall empty box.
  const fill = bare;

  const body = (
    <>
      {bare ? null : <FreshnessLine meta={meta} pending={pending && !backendDown} now={now} />}
      {backendDown ? (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTitle>{backendDownCopy(backendDown.system, backendDown.lastLoad)}</AlertTitle>
          <AlertDescription>Damien is the contact while it&rsquo;s down.</AlertDescription>
        </Alert>
      ) : pending ? (
        // Reserved at final dimensions so nothing reflows when content fills.
        <div className="space-y-3 py-2">
          <Skeleton className="h-8 w-full max-w-xl" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        children
      )}
    </>
  );

  return (
    <section aria-label={title} className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}>
      {headerIsEmpty ? null : header}
      <PermissionGate
        decision={decision}
        areaName={areaName}
        ownerName={ownerName}
        fill={fill}
      >
        {pending && tabs.length === 0 ? (
          // Tabs not yet known: the bar is reserved at final height so it does
          // not push content down when it arrives, and the body still renders.
          <>
            <ModuleTabsSkeleton />
            {body}
          </>
        ) : (
          <ModuleTabs
            tabs={tabs}
            activeTabKey={activeTabKey}
            onOpenTab={onOpenTab ?? (() => undefined)}
            fill={fill}
            counts={tabCounts}
          >
            {body}
          </ModuleTabs>
        )}
      </PermissionGate>
    </section>
  );
}
